import { Box, Caption, Card, Divider, Heading, StyledIcon } from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import { DesktopIcon } from "./icons/DesktopIcon";
import { LaptopIcon } from "./icons/LaptopIcon";
import { MobileIcon } from "./icons/MobileIcon";
import { MonitorIcon } from "./icons/MonitorIcon";
import { OthersIcon } from "./icons/OthersIcon";
import { TabletIcon } from "./icons/TabletIcon";
import { JobsStatusBadge } from "./job-status-badge/JobsStatusBadge";
import { JobStatusBadge } from "./job-status-badge/JobStatusBadge";
import style from "./total-imported-assets-card.scss";
import DataBearingIcon from "components/icons/DataBearing";
import { groupDeviceBreakdowns } from "domain/asset-management/deviceTypeGroups";
import { AssetImportJob } from "services/asset-management/AssetImportJobService";
import { AssetDeviceBreakdown, AssetOverview } from "services/asset-management/AssetService";
import { resolveColor } from "utils/commonFunctions";

interface BadgeProps {
    variant: "Neutral" | "Info";
    children: React.ReactNode;
    dataType: string;
}

const Badge = (props: BadgeProps) => {
    const color =
        props.variant === "Info"
            ? "var(--app-dataBearing-badge-backgroundColor)"
            : "var(--app-nonDataBearing-badge-backgroundColor)";

    return (
        <Box
            verticalAlignment={"Center"}
            horizontalAlignment={"Center"}
            gap={"Xxxs"}
            className={style.badgeBase}
            style={{ backgroundColor: color }}
            datatype={props.dataType}
        >
            {props.children}
        </Box>
    );
};

interface AssetDetails {
    color: string;
    label: string;
    caption?: string;
    icon: React.ReactNode;
    iconBackgroundColor: string;
}

// Asset groups are dynamic. Only the groups listed here get dedicated styling, others fall back to generic styling.
const KNOWN_ASSET_DETAILS: Record<string, Pick<AssetDetails, "color" | "icon" | "iconBackgroundColor">> = {
    smartphone: {
        color: "--app-chart-chartFirstSet",
        icon: <MobileIcon />,
        iconBackgroundColor: "--app-mobile-icon-backgrounColor",
    },
    mobile: {
        color: "--app-chart-chartFirstSet",
        icon: <MobileIcon />,
        iconBackgroundColor: "--app-mobile-icon-backgrounColor",
    },
    laptop: {
        color: "--app-chart-chartSecondSet",
        icon: <LaptopIcon />,
        iconBackgroundColor: "--app-laptop-icon-backgrounColor",
    },
    tablet: {
        color: "--app-chart-chartThirdSet",
        icon: <TabletIcon />,
        iconBackgroundColor: "--app-tablet-icon-backgrounColor",
    },
    desktop: {
        color: "--app-chart-chartSeventhSet",
        icon: <DesktopIcon />,
        iconBackgroundColor: "--app-desktop-icon-backgrounColor",
    },
    display: {
        color: "--app-chart-chartEighthSet",
        icon: <MonitorIcon />,
        iconBackgroundColor: "--app-monitor-icon-backgrounColor",
    },
    monitor: {
        color: "--app-chart-chartEighthSet",
        icon: <MonitorIcon />,
        iconBackgroundColor: "--app-monitor-icon-backgrounColor",
    },
    server: {
        color: "--app-chart-chartSeventhSet",
        icon: <DesktopIcon />,
        iconBackgroundColor: "--app-desktop-icon-backgrounColor",
    },
    router: {
        color: "--app-chart-chartNinthSet",
        icon: <OthersIcon />,
        iconBackgroundColor: "--app-others-icon-backgrounColor",
    },
};

const findKnownAssetDetails = (assetType: string) => {
    const normalized = assetType
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
    if (KNOWN_ASSET_DETAILS[normalized]) {
        return KNOWN_ASSET_DETAILS[normalized];
    }
    if (normalized.includes("phone") || normalized.includes("mobile")) {
        return KNOWN_ASSET_DETAILS.smartphone;
    }
    return undefined;
};

const OTHERS_DEVICE_TYPE = "others";

// Asset types without dedicated styling are shown together instead of as one row each, and the row goes last.
const groupUnknownAsOthers = (breakdowns: AssetDeviceBreakdown[]) => {
    const groupedBreakdowns = groupDeviceBreakdowns(breakdowns);
    const withOthers = groupDeviceBreakdowns(
        groupedBreakdowns.map((asset) =>
            findKnownAssetDetails(asset.deviceType) ? asset : { ...asset, deviceType: OTHERS_DEVICE_TYPE }
        )
    );
    return [
        ...withOthers.filter((asset) => asset.deviceType !== OTHERS_DEVICE_TYPE),
        ...withOthers.filter((asset) => asset.deviceType === OTHERS_DEVICE_TYPE),
    ];
};

const FALLBACK_CHART_COLORS = ["--app-chart-chartFifthSet", "--app-chart-chartSixthSet", "--app-chart-chartNinthSet"];

const toDisplayName = (assetType: string) => assetType.charAt(0).toUpperCase() + assetType.slice(1).replace(/_/g, " ");

interface Props {
    data: AssetOverview;
    groupJobsData: AssetImportJob[];
}

export const TotalImportedAssetsCard = (props: Props) => {
    const { t } = useTranslation();

    const getAssetDetails = (assetType: string, index: number): AssetDetails => {
        const known = findKnownAssetDetails(assetType);
        if (known) {
            return {
                ...known,
                label: toDisplayName(assetType),
                caption: t(`AssetManagement.overview.totalAssetsCard.helptext.${assetType}`, { defaultValue: "" }),
            };
        }

        return {
            color: FALLBACK_CHART_COLORS[index % FALLBACK_CHART_COLORS.length],
            label: toDisplayName(assetType),
            caption: undefined,
            icon: <OthersIcon />,
            iconBackgroundColor: "--app-others-icon-backgrounColor",
        };
    };

    const calculatePercentage = (count: number) => {
        if (count === 0) {
            return 0;
        }

        return Math.round((count / props.data.totalAssets) * 1_000) / 10;
    };

    const breakdown = groupUnknownAsOthers(props.data.breakdown);
    const orderedAssets = [
        ...breakdown.filter((asset) => asset.count > 0),
        ...breakdown.filter((asset) => asset.count === 0),
    ];

    return (
        <Card padding="None" dataType="TOTAL_IMPORTED_ASSETS" className={style.card}>
            <Box columnLayout padding={"Md"}>
                <Box flexWrap={"Wrap"} justifyContent={"SpaceBetween"} gap={"Xxs"}>
                    <Box flexWrap={"Wrap"} gap={"Sm"} className={style.header}>
                        <Box gap={"Xxxs"} className={style.header}>
                            <Heading variant={"h1"} className={style.heading}>
                                {props.data.totalAssets}
                            </Heading>

                            {props.data.totalAssets === 1
                                ? t("AssetManagement.asset").toLowerCase()
                                : t("AssetManagement.assets").toLowerCase()}
                        </Box>
                    </Box>

                    <Box flexWrap={"Wrap"} verticalAlignment="Center" className={style.badgeContainer}>
                        <Box gap={"Xxs"} verticalAlignment="Center">
                            <Badge variant="Info" dataType="DATA_BEARING">
                                <DataBearingIcon color={resolveColor("--app-dataBearing-badge-iconColor")} size={14} />
                                <Caption>{t("AssetManagement.dataBearingFilter.dataBearing")}</Caption>
                            </Badge>
                            <strong>{props.data.trackedDataBearingAssets}</strong>
                        </Box>
                        <Box gap={"Xxs"} verticalAlignment="Center">
                            <Badge variant="Neutral" dataType="NON_DATA_BEARING">
                                <Caption> {t("AssetManagement.dataBearingFilter.nonDataBearing")}</Caption>
                            </Badge>
                            <strong>{props.data.nonDataBearingAssets}</strong>
                        </Box>
                        <Divider direction="Vertical" className={style.divider} />
                        {props.groupJobsData.length === 1 && <JobStatusBadge job={props.groupJobsData[0]} />}

                        {props.groupJobsData.length > 1 && <JobsStatusBadge jobs={props.groupJobsData} />}
                    </Box>
                </Box>
            </Box>

            <Divider />

            <Box columnLayout paddingLeft="Sm" paddingRight="Sm" paddingTop="Sm" paddingBottom={"Md"} gap={"Sm"}>
                {orderedAssets.map((asset, index) => {
                    const assetDetails = getAssetDetails(asset.deviceType, index);

                    const hasAssets = asset.count > 0;
                    const percentage = asset.count ? calculatePercentage(asset.count) : 0;

                    return (
                        <Box
                            key={asset.deviceType}
                            gap={"Sm"}
                            className={!hasAssets ? style.disabled : undefined}
                            datatype={asset.deviceType}
                        >
                            <StyledIcon
                                size={58}
                                icon={assetDetails.icon}
                                backgroundColor={assetDetails.iconBackgroundColor}
                                variant="Square"
                            />

                            <Box
                                columnLayout
                                justifyContent={"SpaceBetween"}
                                className={classNames(style.chart, !hasAssets && style.justifyCenter)}
                            >
                                <Box justifyContent="SpaceBetween" gap={"Sm"} className={style.alignCenter}>
                                    <Box gap={"Xxs"}>
                                        <Heading variant="SUBTITLE_2">{assetDetails.label}</Heading>
                                        {hasAssets && asset.hasDataBearing && (
                                            <Badge variant="Info" dataType="DATA_BEARING">
                                                <DataBearingIcon
                                                    size={12}
                                                    color={resolveColor("--app-dataBearing-badge-iconColor")}
                                                />
                                                <Caption>{t("AssetManagement.dataBearingFilter.dataBearing")}</Caption>
                                            </Badge>
                                        )}
                                        {hasAssets && asset.hasNonDataBearing && (
                                            <Badge variant="Neutral" dataType="NON_DATA_BEARING">
                                                <Caption>
                                                    {t("AssetManagement.dataBearingFilter.nonDataBearing")}
                                                </Caption>
                                            </Badge>
                                        )}
                                    </Box>
                                    <Heading variant="SUBTITLE_2">{asset.count}</Heading>
                                </Box>

                                {hasAssets && (
                                    <Box justifyContent="SpaceBetween">
                                        <Caption greyTextStyle>{assetDetails.caption}</Caption>
                                        <Caption greyTextStyle>
                                            {t("AssetManagement.overview.riskLevelSummaryCards.ofFleet", {
                                                percentage: percentage,
                                            })}
                                        </Caption>
                                    </Box>
                                )}

                                {hasAssets && (
                                    <Box>
                                        <div
                                            style={{
                                                backgroundColor: "var(--theme-background-layer)",
                                                width: "100%",
                                                height: "8px",
                                                borderRadius: "4px",
                                            }}
                                        >
                                            <div
                                                className={style.barFill}
                                                style={{
                                                    backgroundColor: `var(${assetDetails.color})`,
                                                    width: `${percentage}%`,
                                                }}
                                            />
                                        </div>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Card>
    );
};
